import { Input, Menu, Tabs } from '@recruit-robin/robin-theme/web-components';
import { memo, useContext, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { useContextSelector } from 'use-context-selector';

import { Vacancy } from 'clients/AutoCompleteClient/types';
import { VacancyList } from 'components/ui/molecules/VacancyList/VacancyList';
import { CreateJob } from 'components/ui/molecules/VacancyList/components/CreateJob/CreateJob';
import { logger } from 'config/logger';
import { useEnrichedVacanciesQuery } from 'hooks/reachout/useEnrichedVacanciesQuery';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useIndeedJobListQuery } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedJobListQuery';
import { useFeatureToggles } from 'shared/legacy/hooks/useFeatureToggles';
import { useUnsavedChanges } from 'stores/unsaved-changes/hooks/useUnsavedChanges';
import { UnsavedChangesArea } from 'stores/unsaved-changes/unsaved-changes.types';
import { EnrichVacancyModalContext } from '../EnrichVacancyModalContext/EnrichVacancyModalContext';
import { EnrichVacancyModalType } from '../EnrichVacancyModalContext/EnrichVacancyModalContext.types';
import { useStyles } from './VacancySideList.styles';
import { useVacancyActions } from './useVacancyActions';

export const VacancySideList = memo(() => {
  const { dictionary } = useContext(LocalizationContext);
  const classes = useStyles();
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState(search);

  useDebounce(() => setSearchDebounced(search), 230, [search]);

  const modalType = useContextSelector(EnrichVacancyModalContext, (ctx) => ctx.modalType);
  const {
    selectedVacancy,
    handleCreateJob: onCreateJob,
    handleDeleteVacancy: onDeleteVacancy,
    handleEditVacancy: onEditVacancy,
    handleSelectVacancy: onSelectVacancy,
  } = useVacancyActions();

  const { isFeatureToggleEnabled } = useFeatureToggles();

  const { withNavigationGuard } = useUnsavedChanges([
    UnsavedChangesArea.EnrichVacancyModal_JobPreview,
    ...(isFeatureToggleEnabled((f) => f.RJ_COMMUNICATION_HUB)
      ? [UnsavedChangesArea.EnrichVacancyModal_EnrichedVersion]
      : []),
  ]);

  const { data: atsVacancyList, isFetching } = useEnrichedVacanciesQuery({
    search: searchDebounced,
    indeedOnly: modalType === EnrichVacancyModalType.Indeed,
  });

  const { data: indeedJobs, isLoading: isIndeedJobsLoading } = useIndeedJobListQuery();

  const isSearching =
    search !== searchDebounced || isFetching || (modalType === EnrichVacancyModalType.Indeed && isIndeedJobsLoading);

  const indeedJobsFiltered = useMemo(() => {
    return indeedJobs.filter((job) => job.name.toLowerCase().includes(searchDebounced.toLowerCase()));
  }, [indeedJobs, searchDebounced]);

  const vacancyList = useMemo<Vacancy[]>(() => {
    if (modalType !== EnrichVacancyModalType.Indeed) return atsVacancyList;

    const indeedVacancyList = [...atsVacancyList];
    const indeedVacancyListKeys = indeedVacancyList.map((job) => job.job_indeed_id);

    indeedJobsFiltered.forEach((job) => {
      if (job.key) {
        if (!indeedVacancyListKeys.includes(job.key)) {
          indeedVacancyList.push({
            name: job.name,
            value: job.key,
            job_indeed_id: job.key,
            origin_job_title: job.name,
            type: 'INDEED',
            originalIndeedJob: job,
          });
        }
      } else {
        logger.error(`Indeed job key is not defined: ${JSON.stringify(job)}`);
      }
    });

    return indeedVacancyList.map((job) => {
      if (job.type === 'INDEED' && indeedVacancyListKeys.includes(job.value)) {
        const indedJob = indeedJobsFiltered.find((j) => j.key === job.value);

        return { ...job, originalIndeedJob: indedJob };
      }

      return job;
    });
  }, [indeedJobsFiltered, atsVacancyList, modalType]);

  const handleCreateJob = () => {
    withNavigationGuard(() => {
      onCreateJob();
    });
  };

  const handleSelectVacancy = (vacancy: Vacancy) => {
    withNavigationGuard(() => {
      onSelectVacancy(vacancy);
    });
  };

  const handleEditVacancy = (vacancy: Vacancy) => {
    withNavigationGuard(() => {
      onEditVacancy(vacancy);
    });
  };

  const handleDeleteVacancy = (vacancy: Vacancy) => {
    withNavigationGuard(() => {
      onDeleteVacancy(vacancy);
    });
  };

  return (
    <Menu.Root customCss={classes.sideList}>
      <Input
        customCss={classes.searchInput}
        placeholder={dictionary.searchVacancy}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <Tabs.Root value={modalType}>
        <Tabs.List>
          <Tabs.Indicator />
          <Tabs.Trigger customCss={classes.tab} value={modalType}>
            {modalType === EnrichVacancyModalType.Ats && dictionary.atsJobs}
            {modalType === EnrichVacancyModalType.Indeed && dictionary.indeedJobs}
          </Tabs.Trigger>

          <CreateJob customCss={classes.createJob} onCreateJob={handleCreateJob} selectedVacancy={selectedVacancy} />
        </Tabs.List>
        <Tabs.Content customCss={classes.tabContent} value={modalType}>
          {isSearching ? (
            Array.from({ length: 4 }).map((_, i) => <Menu.Item.Skeleton key={i} />)
          ) : (
            <VacancyList
              onCreateJob={handleCreateJob}
              selectedVacancy={selectedVacancy}
              onSelectVacancy={handleSelectVacancy}
              onDeleteVacancy={handleDeleteVacancy}
              onEditVacancy={handleEditVacancy}
              vacanciesList={vacancyList}
              showEnrichedVacancyButton={false}
              disableTooltip
            />
          )}
        </Tabs.Content>
      </Tabs.Root>
    </Menu.Root>
  );
});
